/**
 * `<ResultDetailDrawer>` v2 — Phase 4e of the UI overhaul.
 *
 * The click-through note preview. Opens when a `<ResultCard>` is clicked
 * (`useSearchViewStore.detailDrawerNoteId`). Fetches the full `NoteView` via
 * `useNoteQuery` — body + companion (entities, auto-hashtags, action items,
 * topics, chunks), all embedding-free by construction (conventions.md §6).
 * "Open in editor" hands off to Agent 9's `/editor/:noteId`.
 *
 * Renders as the right-most column of the search layout grid when open
 * (`.se-layout.with-drawer`); hidden on narrow viewports.
 */

import { useNavigate } from 'react-router-dom';
import { EmptyState, Icon, LoadingState, NoteLifecycleBadges } from '@/components';
import { Chip, LabelMono } from '@/components/ui';
import { useNoteQuery } from '@/hooks';
import { toEditor } from '@/routes';
import type { NoteId } from '@/types';

interface ResultDetailDrawerProps {
  noteId: NoteId | null;
  onClose: () => void;
}

export function ResultDetailDrawer({
  noteId,
  onClose,
}: ResultDetailDrawerProps): JSX.Element | null {
  const navigate = useNavigate();
  const noteQuery = useNoteQuery(noteId);

  if (noteId === null) return null;

  return (
    <aside className="se-detail-drawer" aria-label="Note detail">
      <header style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <h2 style={{ flex: 1 }}>Note detail</h2>
        <button
          type="button"
          className="se-detail-close"
          aria-label="Close detail"
          onClick={onClose}
        >
          <Icon name="x" size={14} />
        </button>
      </header>

      {noteQuery.isLoading && <LoadingState variant="inline" label="Loading note…" />}
      {noteQuery.isError && (
        <EmptyState title="Could not load this note" detail="See the notification for details." />
      )}

      {noteQuery.data !== undefined && (
        <>
          <div>
            <div
              style={{
                fontFamily: 'var(--font-ui)', /* U2: note titles are user content, not a brand moment */
                fontWeight: 600,
                fontSize: 18,
                color: 'var(--fg)',
                marginBottom: 4,
              }}
            >
              {noteQuery.data.note.title || 'Untitled'}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: 'var(--fg-3)',
                fontFamily: 'var(--font-mono)',
              }}
            >
              <span>{noteQuery.data.note.source}</span>
              <span>·</span>
              <span>{new Date(noteQuery.data.note.updatedAt).toLocaleString()}</span>
            </div>
            <div style={{ marginTop: 6 }}>
              <NoteLifecycleBadges note={noteQuery.data.note} />
            </div>
          </div>

          <p
            style={{
              fontSize: 13,
              lineHeight: 1.55,
              color: 'var(--fg-2)',
              whiteSpace: 'pre-wrap',
              margin: 0,
            }}
          >
            {noteQuery.data.body.slice(0, 600)}
            {noteQuery.data.body.length > 600 ? '…' : ''}
          </p>

          {noteQuery.data.companion !== null && (
            <>
              {noteQuery.data.companion.entities.length > 0 && (
                <div>
                  <LabelMono>Entities</LabelMono>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                    {noteQuery.data.companion.entities.map((e) => (
                      <Chip key={e.entityId}>{e.canonical}</Chip>
                    ))}
                  </div>
                </div>
              )}

              {noteQuery.data.companion.topics.length > 0 && (
                <div>
                  <LabelMono>Topics</LabelMono>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                    {noteQuery.data.companion.topics.map((t) => (
                      <Chip key={t.topicId}>{t.label ?? t.topicId}</Chip>
                    ))}
                  </div>
                </div>
              )}

              {noteQuery.data.companion.actionItems.length > 0 && (
                <div>
                  <LabelMono>Action items ({noteQuery.data.companion.actionItems.length})</LabelMono>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12.5 }}>
                    {noteQuery.data.companion.actionItems.map((a) => (
                      <li key={a.actionItemId} style={{ marginBottom: 4 }}>
                        {a.text}{' '}
                        <span style={{ color: 'var(--fg-4)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                          [{a.status}]
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {noteQuery.data.companion === null && (
            <EmptyState
              kind="indexing"
              title="Companion not ready"
              detail={`Extraction status: ${noteQuery.data.extractionStatus}`}
            />
          )}

          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => navigate(toEditor(noteId))}
          >
            Open in editor →
          </button>
        </>
      )}
    </aside>
  );
}
